/**
 * Gradient sky with sun glow (GDD §3.1; concept L628). Also rendered into the PMREM environment map so
 * metal, glass and water reflect it (image-based lighting).
 */
import {
  BackSide,
  Color,
  Mesh,
  MeshStandardMaterial,
  PMREMGenerator,
  Scene,
  ShaderMaterial,
  SphereGeometry,
  Vector3,
  type Object3D,
  type Texture,
  type WebGLRenderer,
} from 'three';
import type { Look } from './looks.ts';

export function makeSky(look: Look, sunDir: Vector3): Mesh {
  const mat = new ShaderMaterial({
    side: BackSide,
    depthWrite: false,
    fog: false,
    uniforms: {
      top: { value: new Color(look.top) },
      mid: { value: new Color(look.mid) },
      hor: { value: new Color(look.hor) },
      sunDir: { value: sunDir.clone() },
      sunCol: { value: new Color(look.sunCol) },
    },
    vertexShader:
      'varying vec3 vDir; void main(){ vDir = normalize(position); vec4 p = projectionMatrix * modelViewMatrix * vec4(position, 1.0); gl_Position = p.xyww; }',
    fragmentShader:
      'uniform vec3 top; uniform vec3 mid; uniform vec3 hor; uniform vec3 sunDir; uniform vec3 sunCol; varying vec3 vDir;' +
      'void main(){ vec3 d = normalize(vDir); float y = d.y; vec3 c = mix(hor, mid, smoothstep(0.0, 0.2, y)); c = mix(c, top, smoothstep(0.2, 0.8, y)); c = mix(c, hor * 0.55, smoothstep(0.0, -0.35, y));' +
      'float s = max(dot(d, normalize(sunDir)), 0.0); c += sunCol * (pow(s, 1400.0) * 9.0 + pow(s, 16.0) * 0.5 + pow(s, 3.0) * 0.14); gl_FragColor = vec4(c, 1.0);\n#include <tonemapping_fragment>\n#include <colorspace_fragment>\n}',
  });
  const m = new Mesh(new SphereGeometry(1800, 40, 20), mat);
  m.renderOrder = -10;
  m.frustumCulled = false;
  return m;
}

/**
 * three.js r163+ ignores a material's envMapIntensity when lighting comes from scene.environment (it uses
 * scene.environmentIntensity instead); r159, the concept's version, applied each material's own value. Assigning
 * the environment to every lit material explicitly restores the concept's per-material intensities (terrain 0.35,
 * trees 0.5, rocks 0.4, water 0.9, …) without changing any number.
 */
export function bindEnvironment(root: Object3D, env: Texture): void {
  root.traverse((o) => {
    const mat = (o as Mesh).material;
    if (!mat) return;
    for (const m of Array.isArray(mat) ? mat : [mat]) {
      if (m instanceof MeshStandardMaterial && m.envMap === null) {
        m.envMap = env;
        m.needsUpdate = true;
      }
    }
  });
}

/** Prefiltered environment from the sky (concept L659–L660). */
export function skyEnvironment(renderer: WebGLRenderer, look: Look, sunDir: Vector3): Texture {
  const envScene = new Scene();
  envScene.add(makeSky(look, sunDir));
  // r186 prefilters with GGX importance sampling (r159 used a Gaussian chain); with per-material intensities bound
  // (bindEnvironment) the result matches the concept within 1% brightness and SSIM 0.998 (M1 finding).
  const pmrem = new PMREMGenerator(renderer);
  const rt = pmrem.fromScene(envScene, 0.02, 1, 5000);
  pmrem.dispose();
  return rt.texture;
}
